import { CAMPAIGNS } from 'constants/Entities';
import { axios } from './axios';
import { handleErrors } from './utils';

export function getAccounts(id) {
    if (!id) return Promise.reject('Identifier is needed');
    return new Promise((resolve, reject) => {
        axios
            .get(`${CAMPAIGNS.endpoint}/${id}/accounts`)
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

export function getAccountsPaginated({ id, isCompleted = true, page = 0, salesRepId }) {
    if (!id) return Promise.reject('Identifier is needed');
    const completed = isCompleted ? 1 : 0;

    let queryParams = `completed=${completed}&page=${page}&count=60`;
    if (salesRepId) queryParams = `${queryParams}&salesrepid=${salesRepId}`;

    return new Promise((resolve, reject) => {
        axios
            .get(`${CAMPAIGNS.endpoint}/${id}/accounts?${queryParams}`)
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

export function getQuestions(id, filters) {
    if (!id) return Promise.reject('Identifier is needed');
    return new Promise((resolve, reject) => {
        axios
            .get(`${CAMPAIGNS.endpoint}/${id}/questions`, {
                params: filters,
            })
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

export function getNotifications() {
    return new Promise((resolve, reject) => {
        axios
            .get(`${CAMPAIGNS.endpoint}/notifications/info`)
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

export function readNotifications(ids) {
    return new Promise((resolve, reject) => {
        axios
            .put(`${CAMPAIGNS.endpoint}/notifications/info`, {
                campaignId: ids.map(String),
            })
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

// =============================================================================
// Marketing Email Templates API (v2)
// =============================================================================

/**
 * GET `/mailing/v2/template`. Marketing email templates list.
 * Returns array of `{ id, name, strBody, strJson }`.
 * @param {string} [name] Optional name filter (backend query param).
 * @returns {Promise<Record<string, unknown>[]>}
 */
export function getMarketingEmailTemplates(name) {
    return new Promise((resolve, reject) => {
        const trimmedName = typeof name === 'string' ? name.trim() : '';
        const params = trimmedName ? { name: trimmedName } : {};

        axios
            .get('/mailing/v2/template', { params })
            .then(({ data }) => {
                if (Array.isArray(data)) {
                    resolve(data);
                    return;
                }
                resolve([]);
            })
            .catch(handleErrors(reject));
    });
}

/**
 * POST `/mailing/v2/template`. Create marketing email template.
 * Transforms frontend fields (html, topolConfig) to backend format (strBody, strJson).
 *
 * @param {{ name: string, html?: string, strBody?: string, topolConfig?: string|object }} template
 * @returns {Promise<unknown>}
 */
export function createMarketingEmailTemplate(template) {
    return new Promise((resolve, reject) => {
        const strJson = template.topolConfig
            ? typeof template.topolConfig === 'string'
                ? template.topolConfig
                : JSON.stringify(template.topolConfig)
            : undefined;

        const payload = {
            name: template.name,
            strBody: template.html ?? template.strBody,
            ...(strJson ? { strJson } : {}),
        };

        axios
            .post('/mailing/v2/template', payload)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

/**
 * PUT `/mailing/v2/template/{id}`. Update marketing email template.
 *
 * @param {string|number} id
 * @param {{ name: string, html?: string, strBody?: string, topolConfig?: string|object }} template
 * @returns {Promise<unknown>}
 */
export function updateMarketingEmailTemplate(id, template) {
    return new Promise((resolve, reject) => {
        const strJson = template.topolConfig
            ? typeof template.topolConfig === 'string'
                ? template.topolConfig
                : JSON.stringify(template.topolConfig)
            : undefined;

        const payload = {
            name: template.name,
            strBody: template.html ?? template.strBody,
            ...(strJson ? { strJson } : {}),
        };

        axios
            .put(`/mailing/v2/template/${id}`, payload)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

/**
 * DELETE `/mailing/v2/template/{id}`. Delete marketing email template.
 *
 * @param {string|number} id
 * @returns {Promise<unknown>}
 */
export function deleteMarketingEmailTemplate(id) {
    return new Promise((resolve, reject) => {
        axios
            .delete(`/mailing/v2/template/${id}`)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}
